import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuote from '@mui/icons-material/RequestQuote';
import {
  Alert,
  alpha,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useCallback, useMemo, useState } from 'react';
import { type FieldValues, FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import DialogBackNextActions from '~/components/DialogBackNextActions';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { DocumentPreview } from '~/components/documentPreview';
import { useCertificateForm } from '~/components/uploadDocument/certificateForm';
import type { InspectionReportFormData } from '~/components/uploadDocument/inspectionReportForm';
import { useInspectionReportForm } from '~/components/uploadDocument/inspectionReportForm';
import type { SupportingDataDto } from '~/components/uploadDocument/SupportingDataForm';
import { useSupportingDataForm } from '~/components/uploadDocument/SupportingDataForm';
import { useViolationForm } from '~/components/uploadDocument/violationForm';
import { dateToDateString } from '~/components/utils/dateUtils';
import type { API_CertificateCreateRequestResponse } from '~/data/schemas/certificate';
import type { API_InspectionReportCreateResponse } from '~/data/schemas/inspectionReport';
import { type InvoiceForm, InvoiceKind, InvoiceStatus } from '~/data/schemas/invoice';
import type { TicketDto } from '~/data/schemas/ticket';
import { useResizeObserver } from '~/hooks/useResizeObserver';

import { UploadDocumentType } from './AddManuallyDropdown';
import { DefaultFields, DefaultForm } from './DefaultFields';
import { useInvoiceForm } from './InvoiceForm';
import SelectDocumentTypeDropdown from './SelectDocumentTypeDropdown';
import { useTicketForm } from './TicketForm';
import type { LiftAIDocumentUntagged } from './UploadDocumentDialogFlow';

interface ISelectUploadDocumentTypeDialogProps {
  open: boolean;
  onCancel: () => void;
  onDone: (taggedDocuments: LiftAIDocumentTagged[]) => void;
  documents: LiftAIDocumentUntagged[];
  /** When provided, skips the document type selection step and goes directly to the form. */
  initialDocumentType?: UploadDocumentType;
  /** Extra form data merged into the initial form data when `initialDocumentType` is set. */
  initialFormData?: Record<string, unknown>;
}

type InvoiceEditable =
  | {
      /** `editInvoice` enables the form in edit mode for the invoice */
      editInvoice: boolean;
      /** `invoiceData` contains the invoice data to be edited */
      invoiceData: InvoiceForm;
    }
  | {
      editInvoice?: never;
      invoiceData?: never;
    };

export type LiftAIDocumentTagged = LiftAIDocumentUntagged & {
  documentType: UploadDocumentType;
  formData: unknown;
};

type SelectUploadDocumentTypeState = {
  currentDocumentIndex: number;
  taggedDocuments: LiftAIDocumentTagged[];
  lastTicketId: string | null;
  lastCertificateId: string | null;
  lastInspectionReportId: string | null;
  lastInvoiceId: string | null;
  lastInvoiceKind: UploadDocumentType | null;
  lastPropertyId: string | null;
  lastCarIds: string[];
  invoiceSelected: boolean;
  proposalSelected: boolean;
  ticketSelected: boolean;
  certificateSelected: boolean;
  inspectionReportSelected: boolean;
  supportingDataMovingForward?: boolean;
  remainingPages?: number;
  subsequentPagesAsContinued?: boolean;
} & (
  | { name: 'fillingOutForm'; selectedDocumentType: UploadDocumentType | null }
  | { name: 'selecting'; selectedDocumentType: UploadDocumentType | null }
);

export type LiftAIFormProps<T = unknown, TSubmit = unknown> = {
  children: React.ReactNode;
  document?: LiftAIDocumentTagged;
  initialData?: T;
  onSubmit(data: TSubmit): void;
};

// `UseFormReturn`'s second type argument is the form context, not the submit
// payload. The old `TSubmit` parameter sat in that slot, where `useForm`'s
// `any` context satisfied it unconditionally, so it never constrained
// anything. Each concrete form component already names its own submit payload
// through `LiftAIFormProps<T, TSubmit>`, so the parameter is dropped here.
//
// `TParsed` is `useForm`'s third argument: the values the zod resolver
// produces. It diverges from `T` only where a schema uses `.default()`,
// `z.coerce`, or `.transform()`, so it defaults to `T`.
export type LAIUseFormReturn<T extends FieldValues, TParsed extends FieldValues = T> = {
  hookForm: UseFormReturn<T, unknown, TParsed>;
  Form: React.FC<LiftAIFormProps<T>>;
  Fields: React.FC<{ documentType?: UploadDocumentType; disabled?: boolean }>;
};

type UseCurrentFormReturn = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hookForm: UseFormReturn<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Form: React.FC<LiftAIFormProps<any>>;
  Fields: React.FC<{ documentType?: UploadDocumentType; disabled?: boolean }>;
};

const useCurrentForm = (
  documentType: UploadDocumentType = UploadDocumentType.initial,
): UseCurrentFormReturn => {
  const ticketForm = useTicketForm();
  const invoiceForm = useInvoiceForm();
  const supportingDataForm = useSupportingDataForm();
  const certificateForm = useCertificateForm();
  const violationForm = useViolationForm();
  const inspectionReportForm = useInspectionReportForm();

  const defaults: UseCurrentFormReturn = {
    hookForm: useForm(),
    Form: DefaultForm,
    Fields: DefaultFields,
  };

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (documentType) {
    case UploadDocumentType.ticket:
      return {
        hookForm: ticketForm.hookForm,
        Form: ticketForm.Form,
        Fields: ticketForm.Fields,
      };
    case UploadDocumentType.invoice:
    case UploadDocumentType.proposal:
    case UploadDocumentType.credit:
      return {
        hookForm: invoiceForm.hookForm,
        Form: invoiceForm.Form,
        Fields: invoiceForm.Fields,
      };
    case UploadDocumentType.certificate:
      return {
        hookForm: certificateForm.hookForm,
        Form: certificateForm.Form,
        Fields: certificateForm.Fields,
      };
    case UploadDocumentType.violation:
      return {
        hookForm: violationForm.hookForm,
        Form: violationForm.Form,
        Fields: violationForm.Fields,
      };
    case UploadDocumentType.inspectionReport:
      return {
        hookForm: inspectionReportForm.hookForm,
        Form: inspectionReportForm.Form,
        Fields: inspectionReportForm.Fields,
      };
    case UploadDocumentType.supportingData:
    case UploadDocumentType.invoiceContinued:
    case UploadDocumentType.proposalContinued:
    case UploadDocumentType.ticketContinued:
    case UploadDocumentType.certificateContinued:
    case UploadDocumentType.inspectionReportContinued:
      return {
        hookForm: supportingDataForm.hookForm,
        Form: supportingDataForm.Form,
        Fields: supportingDataForm.Fields,
      };
    default:
      return defaults;
  }
};

const DocumentTypeIcon = ({ documentType }: { documentType: UploadDocumentType }) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (documentType) {
    case UploadDocumentType.ticket:
      return <ReceiptLongOutlined />;
    case UploadDocumentType.invoice:
    case UploadDocumentType.proposal:
    case UploadDocumentType.credit:
      return <RequestQuote />;
    default:
      return null;
  }
};

// Credits are approved when filed.
const CREDIT_INITIAL_FORM_DATA = {
  kind: InvoiceKind.Credit,
  status: InvoiceStatus.Approved,
  creditTarget: 'account',
  keepOnFileOnly: false,
};

const getInitialFormData = (documentType: UploadDocumentType): Record<string, unknown> => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (documentType) {
    case UploadDocumentType.invoice:
      return { kind: InvoiceKind.Invoice };
    case UploadDocumentType.proposal:
      return { kind: InvoiceKind.Proposal };
    case UploadDocumentType.credit:
      return CREDIT_INITIAL_FORM_DATA;
    default:
      return {};
  }
};

/**
 * Initial form values for a manually entered inspection report.
 *
 * Every key must be spelled the way `useInspectionReportForm` registers it. This
 * object is handed straight to react-hook-form's `reset()`, which replaces the
 * whole values object, so a misspelled key does not merely fail to prefill — it
 * leaves the real field `undefined`. `inspectionType` defaults to `periodic`, so
 * a mis-keyed payload left the Type select still rendering "Periodic Inspection"
 * while the form held nothing, and re-picking the value already on screen fired
 * no change event. That was AMP-691. The certificate and violation cases were
 * corrected the same way in AMP-420.
 *
 * Typed as `Partial<InspectionReportFormData>` on purpose: excess-property
 * checking makes a wrong key name a compile error rather than a silent blank
 * field. Exported so the regression test binds to this value, not a copy of it.
 *
 * `inspectionDate` uses the local calendar date. `toISOString()` would give the
 * UTC date, which is already tomorrow for a user west of UTC in the evening, so
 * the form would prefill a date the inspection cannot have happened on. This
 * prefill never reached the form before the key rename, so the skew ships the
 * moment it starts working.
 */
export const getInspectionReportInitialFormData = (): Partial<InspectionReportFormData> => ({
  propertyId: '',
  carId: '',
  inspectionDate: dateToDateString(new Date()),
  inspectionType: 'periodic',
  customType: '',
  status: 'passed',
  inspectorName: '',
  comments: '',
  violations: [],
});

const SelectUploadDocumentTypeDialog: React.FC<
  ISelectUploadDocumentTypeDialogProps & InvoiceEditable
> = ({
  open,
  onCancel,
  onDone,
  documents,
  editInvoice,
  invoiceData,
  initialDocumentType,
  initialFormData: extraFormData,
}) => {
  const [state, setState] = useState<SelectUploadDocumentTypeState>(() => {
    const skipToForm = initialDocumentType != null;
    return {
      name: skipToForm ? 'fillingOutForm' : 'selecting',
      currentDocumentIndex: 0,
      selectedDocumentType: initialDocumentType ?? null,
      taggedDocuments: documents.map((doc, i) => ({
        ...doc,
        pageNum: i + 1,
        documentType: skipToForm ? initialDocumentType : UploadDocumentType.initial,
        formData: skipToForm
          ? { ...getInitialFormData(initialDocumentType), ...extraFormData }
          : {},
      })),
      lastTicketId: null,
      lastCertificateId: null,
      lastInspectionReportId: null,
      lastInvoiceId: null,
      lastInvoiceKind: null,
      lastPropertyId: null,
      lastCarIds: [],
      invoiceSelected: false,
      proposalSelected: false,
      ticketSelected: false,
      certificateSelected: false,
      inspectionReportSelected: false,
      supportingDataMovingForward: false,
      subsequentPagesAsContinued: false,
    };
  });
  const documentTypes = useMemo(() => {
    const options = [
      UploadDocumentType.invoice,
      UploadDocumentType.ticket,
      state.proposalSelected ? UploadDocumentType.proposalContinued : UploadDocumentType.proposal,
      UploadDocumentType.credit,
      UploadDocumentType.supportingData,
      UploadDocumentType.certificate,
      UploadDocumentType.violation,
      UploadDocumentType.inspectionReport,
    ];

    if (state.invoiceSelected) {
      options.splice(1, 0, UploadDocumentType.invoiceContinued);
    }

    if (state.ticketSelected) {
      options.splice(2, 0, UploadDocumentType.ticketContinued);
    }
    if (state.certificateSelected) {
      options.splice(
        options.indexOf(UploadDocumentType.certificate) + 1,
        0,
        UploadDocumentType.certificateContinued,
      );
    }

    if (state.inspectionReportSelected) {
      options.splice(
        options.indexOf(UploadDocumentType.inspectionReport) + 1,
        0,
        UploadDocumentType.inspectionReportContinued,
      );
    }

    return options;
  }, [
    state.invoiceSelected,
    state.proposalSelected,
    state.ticketSelected,
    state.certificateSelected,
    state.inspectionReportSelected,
  ]);
  const [searchParams] = useSearchParams();
  const propertyIdFromParams = searchParams.get('propertyId');
  const carIdFromParams = searchParams.get('carId');
  const theme = useTheme();
  const { currentDocumentIndex, taggedDocuments } = state;
  const [documentPreviewContainerRef, { height: documentPreviewContainerHeight }] =
    useResizeObserver<HTMLDivElement>();
  const numDocuments = documents.length;
  const currentDocument: LiftAIDocumentTagged | undefined = taggedDocuments[currentDocumentIndex];
  const currentPage = currentDocumentIndex + 1;

  const handleForwardSubsequentPages = useCallback(
    (checked: boolean) => {
      const nextDocs = [...state.taggedDocuments];
      nextDocs[currentDocumentIndex].formData = {
        ...(nextDocs[currentDocumentIndex].formData || {}),
        subsequentPagesAsContinued: checked,
        remainingPages: numDocuments - currentPage,
      };

      setState((state) => ({
        ...state,
        subsequentPagesAsContinued: checked,
        remainingPages: numDocuments - currentPage,
        taggedDocuments: nextDocs,
      }));
    },
    [state.taggedDocuments, currentDocumentIndex, numDocuments, currentPage],
  );

  const handleUploadDocumentTypeSelected = useCallback(
    (documentType: UploadDocumentType) => {
      if (currentDocument.fileType !== 'pdf') {
        return;
      }

      if (editInvoice) {
        return setState((state) => ({
          ...state,
          lastInvoiceId: invoiceData.id,
          lastPropertyId: invoiceData.propertyId,
          lastCarIds: invoiceData.carIds,
          selectedDocumentType: documentType,
        }));
      }

      return setState((state) => ({
        ...state,
        selectedDocumentType: documentType,
      }));
    },
    [currentDocument.fileType, editInvoice, invoiceData],
  );

  const handleNext = useCallback(
    (...args: unknown[]) => {
      if (state.name === 'selecting' && state.selectedDocumentType) {
        const nextDocs = [...state.taggedDocuments];

        const nextDocument = {
          ...currentDocument,
          documentType: state.selectedDocumentType,
        };
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (state.selectedDocumentType) {
          case UploadDocumentType.ticket:
            nextDocument.formData = {
              invoiceId: state.lastInvoiceId,
              propertyId: state.lastPropertyId,
              carIds: state.lastCarIds,
            };
            break;
          case UploadDocumentType.certificate:
            nextDocument.formData = {
              isCarChangeable: !propertyIdFromParams || !carIdFromParams,
              jurisdiction: '',
              dateIssued: '',
              expirationDate: '',
              ...(propertyIdFromParams && { propertyId: propertyIdFromParams }),
              ...(carIdFromParams && { carId: carIdFromParams }),
            };
            break;

          case UploadDocumentType.violation:
            nextDocument.formData = {
              isCarChangeable: !propertyIdFromParams || !carIdFromParams,
              propertyId: propertyIdFromParams ?? '',
              carId: carIdFromParams ?? '',
              code: '',
              location: '',
              correctionDue: '',
              responsibility: 'owner',
              description: '',
            };
            break;
          case UploadDocumentType.inspectionReport:
            nextDocument.formData = getInspectionReportInitialFormData();
            break;
          case UploadDocumentType.invoice:
          case UploadDocumentType.proposal:
          case UploadDocumentType.credit: {
            if (editInvoice) {
              nextDocument.formData = invoiceData;
              setState((curState) => ({ ...curState, lastInvoiceId: invoiceData.id }));
            } else {
              nextDocument.formData = getInitialFormData(state.selectedDocumentType);
            }
            break;
          }
          case UploadDocumentType.ticketContinued:
            nextDocument.formData = {
              ticketId: state.lastTicketId,
              isContinued: true,
            };
            break;

          case UploadDocumentType.certificateContinued:
            nextDocument.formData = {
              isContinued: true,
              certificateId: state.lastCertificateId,
            };
            break;

          case UploadDocumentType.inspectionReportContinued:
            nextDocument.formData = {
              inspectionReportId: state.lastInspectionReportId,
              isContinued: true,
            };
            break;
          case UploadDocumentType.proposalContinued:
          case UploadDocumentType.invoiceContinued:
            nextDocument.formData = {
              invoiceId: state.lastInvoiceId,
              invoiceKind: state.lastInvoiceKind,
              isContinued: true,
            };
            break;
          case UploadDocumentType.supportingData:
            nextDocument.formData = {
              invoiceId: state.lastInvoiceId,
              invoiceKind: state.lastInvoiceKind,
              propertyId: state.lastPropertyId,
              carIds: state.lastCarIds,
              supportingDataMovingForward: state.supportingDataMovingForward,
              remainingPages: state.remainingPages,
            };
            break;
          default:
            nextDocument.formData = {};
        }

        nextDocs[currentDocumentIndex] = nextDocument;

        setState((curState) => {
          return {
            ...curState,
            name: 'fillingOutForm',
            taggedDocuments: nextDocs,
          };
        });
      }
    },
    [
      currentDocument,
      currentDocumentIndex,
      state,
      editInvoice,
      invoiceData,
      propertyIdFromParams,
      carIdFromParams,
    ],
  );

  const handleBack = useCallback(() => {
    if (state.name === 'fillingOutForm') {
      // If we started directly in fillingOutForm (via initialDocumentType) on the first page, cancel
      if (initialDocumentType != null && currentDocumentIndex === 0) {
        onCancel();
        return;
      }
      setState({
        ...state,
        name: 'selecting',
        selectedDocumentType: currentDocument.documentType,
      });
    } else {
      if (currentDocumentIndex === 0) {
        onCancel();
      } else {
        setState((state) => ({
          ...state,
          name: 'fillingOutForm',
          currentDocumentIndex: currentDocumentIndex - 1,
        }));
      }
    }
  }, [state, onCancel, currentDocument, currentDocumentIndex, initialDocumentType]);

  const handleSkip = useCallback(() => {
    if (currentDocumentIndex >= taggedDocuments.length - 1) {
      return onDone([...state.taggedDocuments]);
    }

    if (state.name === 'selecting') {
      setState({
        ...state,
        name: 'selecting',
        currentDocumentIndex: currentDocumentIndex + 1,
      });
    }
  }, [currentDocumentIndex, onDone, state, taggedDocuments.length]);

  const onSubmit = useCallback(
    (formData: unknown) => {
      const nextDocs = [...state.taggedDocuments];
      nextDocs[currentDocumentIndex] = {
        ...currentDocument,
        formData,
      };

      if (currentDocument.documentType === UploadDocumentType.violation) {
        return onDone([...nextDocs]);
      }

      const hasSavedMultipleSupportingData = () =>
        currentDocument.documentType === UploadDocumentType.supportingData &&
        (currentDocument.formData as SupportingDataDto).supportingDataMovingForward;
      const thereAreNotDocumentsLeft = () =>
        state.name === 'fillingOutForm' && currentDocumentIndex + 1 === numDocuments;
      if (
        hasSavedMultipleSupportingData() ||
        thereAreNotDocumentsLeft() ||
        state.subsequentPagesAsContinued
      ) {
        return onDone([...nextDocs]);
      }

      setState((curState) => {
        const {
          lastInvoiceId,
          lastPropertyId,
          lastCarIds,
          lastInvoiceKind = null,
          lastTicketId = null,
          lastCertificateId = null,
          lastInspectionReportId = null,
        } = (() => {
          // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
          switch (currentDocument.documentType) {
            case UploadDocumentType.invoice:
              return {
                lastInvoiceId: (formData as InvoiceForm).id,
                lastPropertyId: (formData as InvoiceForm).propertyId,
                lastCarIds: (formData as InvoiceForm).carIds,
                lastInvoiceKind: UploadDocumentType.invoice,
              };
            case UploadDocumentType.proposal:
              return {
                lastInvoiceId: (formData as InvoiceForm).id,
                lastPropertyId: (formData as InvoiceForm).propertyId,
                lastCarIds: (formData as InvoiceForm).carIds,
                lastInvoiceKind: UploadDocumentType.proposal,
              };
            case UploadDocumentType.credit:
              return {
                lastInvoiceId: (formData as InvoiceForm).id,
                lastPropertyId: (formData as InvoiceForm).propertyId,
                lastCarIds: (formData as InvoiceForm).carIds,
                lastInvoiceKind: UploadDocumentType.credit,
              };
            case UploadDocumentType.ticket:
              return {
                lastInvoiceId: (formData as TicketDto).invoiceId,
                lastPropertyId: (formData as TicketDto).propertyId,
                lastCarIds: (formData as TicketDto).carIds,
                lastTicketId: (formData as TicketDto).id,
              };
            case UploadDocumentType.certificate:
              return {
                lastInvoiceId: curState.lastInvoiceId,
                lastPropertyId: curState.lastPropertyId,
                lastCarIds: curState.lastCarIds,
                lastCertificateId: (formData as API_CertificateCreateRequestResponse).id,
              };
            case UploadDocumentType.inspectionReport:
              return {
                ...curState,
                lastInvoiceId: curState.lastInvoiceId,
                lastPropertyId: curState.lastPropertyId,
                lastCarIds: curState.lastCarIds,
                lastInspectionReportId: (formData as API_InspectionReportCreateResponse).id,
              };
            case UploadDocumentType.violation:
              return {
                ...curState,
                lastPropertyId: curState.lastPropertyId,
              };
            default:
              return {
                lastInvoiceId: curState.lastInvoiceId,
                lastTicketId: curState.lastTicketId,
                lastCertificateId: curState.lastCertificateId,
                lastInspectionReportId: curState.lastInspectionReportId,
                lastPropertyId: curState.lastPropertyId,
                lastCarIds: curState.lastCarIds,
              };
          }
        })();

        const invoiceSelected =
          currentDocument.documentType === UploadDocumentType.invoice ||
          currentDocument.documentType === UploadDocumentType.invoiceContinued;
        const proposalSelected =
          currentDocument.documentType === UploadDocumentType.proposal ||
          currentDocument.documentType === UploadDocumentType.proposalContinued;
        const ticketSelected =
          currentDocument.documentType === UploadDocumentType.ticket ||
          currentDocument.documentType === UploadDocumentType.ticketContinued;

        const certificateSelected =
          currentDocument.documentType === UploadDocumentType.certificate ||
          currentDocument.documentType === UploadDocumentType.certificateContinued;

        const inspectionReportSelected =
          currentDocument.documentType === UploadDocumentType.inspectionReport ||
          currentDocument.documentType === UploadDocumentType.inspectionReportContinued;

        return {
          ...curState,
          name: 'selecting',
          selectedDocumentType: null,
          currentDocumentIndex: currentDocumentIndex + 1,
          taggedDocuments: nextDocs,
          lastTicketId,
          lastCertificateId,
          lastInspectionReportId,
          lastInvoiceId,
          lastInvoiceKind,
          lastPropertyId,
          lastCarIds,
          invoiceSelected,
          proposalSelected,
          ticketSelected,
          certificateSelected,
          inspectionReportSelected,
        };
      });
    },
    [
      currentDocument,
      currentDocumentIndex,
      numDocuments,
      onDone,
      state.subsequentPagesAsContinued,
      state.name,
      state.taggedDocuments,
    ],
  );

  const { hookForm, Form, Fields } = useCurrentForm(currentDocument?.documentType);
  // Read straight off `hookForm.formState`, NOT `useFormState({ control })`.
  //
  // `useCurrentForm` hands back a different form per document type, and the type
  // changes while this component stays mounted: documents seed as
  // `UploadDocumentType.initial`, so the control at mount is the throwaway
  // `useForm()` in `useCurrentForm`'s `defaults`. `useFormState` subscribes in a
  // layout effect keyed on [name, disabled, exact, resyncIfNeeded, snapshot] and
  // NOT on `control` (react-hook-form 7.87), so it would stay bound to that
  // throwaway control forever. `isSubmitting` would never leave `false`, the Next
  // button would never disable, and a double-click mid-save would submit twice.
  //
  // The proxy read is safe here specifically because every `useForm()` behind
  // `useCurrentForm` runs in THIS component's hook chain, so touching
  // `formState.isSubmitting` registers the subscription that re-renders us. The
  // React Compiler declines to memoize this component as a result, which is the
  // correct trade for a control that swaps underneath the hook. Do not "migrate"
  // this back to `useFormState` (AMP-737).
  const { isSubmitting } = hookForm.formState;

  const actionBtns = useMemo(() => {
    const nextEnabled =
      (state.name === 'fillingOutForm' || state.selectedDocumentType != null) && !isSubmitting;
    // Only show next until we can fix the backend update functionality
    // Right now a lot of errors are thrown in the InvoiceSerializer when trying to update an invoice
    // Haven't tested to see what is needed for the other document types
    // When it's fixed we can just remove the `actionBtns` attribute to use the default behavior
    return {
      next: { label: isSubmitting ? 'Saving Page' : 'Next', enabled: nextEnabled },
      ...(state.name === 'selecting' && { skip: { label: 'Skip this Page', enabled: true } }),
    };
  }, [isSubmitting, state]);

  const replaceInvoice =
    editInvoice &&
    state.name === 'selecting' &&
    (state.selectedDocumentType === UploadDocumentType.invoice ||
      state.selectedDocumentType === UploadDocumentType.invoiceContinued ||
      state.selectedDocumentType === UploadDocumentType.proposal ||
      state.selectedDocumentType === UploadDocumentType.proposalContinued ||
      state.selectedDocumentType === UploadDocumentType.credit);

  const isContinued =
    state.selectedDocumentType === UploadDocumentType.ticketContinued ||
    state.selectedDocumentType === UploadDocumentType.invoiceContinued ||
    state.selectedDocumentType === UploadDocumentType.proposalContinued;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth={false}
      sx={{
        // Needed to make the dialog full height so that the DocumentPreview dimensions can be correctly calculated
        '& > .MuiDialog-container > .MuiPaper-root': {
          height: '100%',
        },
      }}
    >
      <FormProvider {...hookForm}>
        <Form document={currentDocument} onSubmit={onSubmit} initialData={currentDocument.formData}>
          {currentDocument.fileType === 'pdf' ? (
            <>
              <DialogTitleWithCloseButton onClose={onCancel} title="Upload Document" />
              <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                <Grid container minHeight={0} flexGrow={1}>
                  <Grid size={{ xs: 7 }} height="100%" ref={documentPreviewContainerRef}>
                    <DocumentPreview
                      documents={taggedDocuments}
                      currentPage={currentPage}
                      containerHeight={documentPreviewContainerHeight}
                    />
                  </Grid>
                  <Grid size={{ xs: 5 }} order={1} px={1}>
                    <Typography variant="body1" mb={2}>
                      {numDocuments > 0 ? `Page ${currentPage} of ${numDocuments}` : null}
                    </Typography>
                    {state.name === 'selecting' ? (
                      <>
                        <Typography variant="h6" mt={6} mb={1}>
                          What type of document is this page?
                        </Typography>
                        <SelectDocumentTypeDropdown
                          onChange={handleUploadDocumentTypeSelected}
                          documentTypes={documentTypes}
                          value={state.selectedDocumentType}
                        />
                        {state.selectedDocumentType === UploadDocumentType.supportingData && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={state.supportingDataMovingForward}
                                onChange={({ target }) => {
                                  setState((curState) => ({
                                    ...curState,
                                    supportingDataMovingForward: target.checked,
                                    remainingPages: numDocuments - currentPage,
                                  }));
                                }}
                              />
                            }
                            label="Mark subsequent pages as Supporting Data"
                            sx={{ marginLeft: theme.spacing(0.5) }}
                          />
                        )}
                        {(replaceInvoice ?? false) ? (
                          <Alert sx={{ marginTop: theme.spacing(4) }} severity="warning">
                            Uploading a new {state.selectedDocumentType} PDF will void the existing{' '}
                            {state.selectedDocumentType} and keep it as supporting data.
                          </Alert>
                        ) : null}
                      </>
                    ) : null}
                    {state.name === 'fillingOutForm' ? (
                      <>
                        <Typography variant="h6" my={1} color="primary">
                          <IconButton
                            color="primary"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.12),
                              marginRight: theme.spacing(2),
                            }}
                          >
                            {currentDocument.documentType ? (
                              <DocumentTypeIcon documentType={currentDocument.documentType} />
                            ) : null}
                          </IconButton>
                          {currentDocument.documentType ?? ''}
                        </Typography>
                        <Fields documentType={currentDocument.documentType} />
                        {isContinued ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={state.subsequentPagesAsContinued}
                                onChange={({ target }) =>
                                  handleForwardSubsequentPages(target.checked)
                                }
                              />
                            }
                            label={`Mark subsequent pages as ${state.selectedDocumentType}`}
                            sx={{ marginLeft: theme.spacing(0.5) }}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </Grid>
                </Grid>
              </DialogContent>
            </>
          ) : (
            <DialogContent>Image Support Not Implemented</DialogContent>
          )}

          <DialogBackNextActions
            onBack={handleBack}
            onNext={handleNext}
            onSkip={handleSkip}
            actionBtns={actionBtns}
          />
        </Form>
      </FormProvider>
    </Dialog>
  );
};

export default SelectUploadDocumentTypeDialog;
